// ABOUTME: Modal for creating workspaces in the admin dashboard.
// ABOUTME: Collects workspace name, slug, tier, and status, then creates the workspace.

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { workspaceApi } from '@/services/api/adminClient';
import type { WorkspaceCreateRequest, WorkspaceListItem, SubscriptionTier } from '@/types/admin';

interface CreateWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (workspace: WorkspaceListItem) => void;
}

const TIERS: SubscriptionTier[] = ['freemium', 'basic', 'standard', 'enterprise', 'enterprise_plus'];

export function CreateWorkspaceModal({
  isOpen,
  onClose,
  onCreated,
}: CreateWorkspaceModalProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [tier, setTier] = useState<SubscriptionTier>('freemium');
  const [status, setStatus] = useState<'active' | 'trial'>('trial');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Auto-generate slug from name
  useEffect(() => {
    const generatedSlug = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 50);
    setSlug(generatedSlug);
  }, [name]);

  const handleCreate = async () => {
    if (!name.trim() || !slug.trim()) {
      setError('Name and slug are required');
      return;
    }

    setError(null);
    setIsCreating(true);

    try {
      const request: WorkspaceCreateRequest = {
        name: name.trim(),
        slug: slug.trim(),
        subscription_tier: tier,
        subscription_status: status,
      };

      const workspace = await workspaceApi.create(request);
      onCreated(workspace);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create workspace');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setName('');
    setSlug('');
    setTier('freemium');
    setStatus('trial');
    setError(null);
    setIsCreating(false);

    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-2xl bg-surface-0 border border-surface-200 shadow-modal rounded-sm overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-surface-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-primary-50 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-surface-800">
                  Create Workspace
                </h2>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-surface-100 rounded-sm transition-all"
            >
              <X className="w-5 h-5 text-surface-500" />
            </button>
          </div>

          <div className="p-6">
            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 rounded-sm bg-error-light border border-error/30 text-error text-sm"
              >
                {error}
              </motion.div>
            )}

            {/* Form */}
            <div className="space-y-4 mb-6">
              <Input
                label="Workspace Name"
                placeholder="My Workspace"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!name.trim() && error ? 'Name is required' : undefined}
              />

              <Input
                label="Slug"
                placeholder="my-workspace"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                helperText="URL-friendly identifier (lowercase letters, numbers, and hyphens only)"
                error={!slug.trim() && error ? 'Slug is required' : undefined}
              />

              <div>
                <label className="block text-sm font-medium text-surface-600 mb-1.5">
                  Subscription Tier
                </label>
                <select
                  value={tier}
                  onChange={(e) => setTier(e.target.value as SubscriptionTier)}
                  className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50 transition-all"
                >
                  {TIERS.map((t) => (
                    <option key={t} value={t}>
                      {t.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-600 mb-1.5">
                  Subscription Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'active' | 'trial')}
                  className="w-full px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50 transition-all"
                >
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button variant="ghost" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                isLoading={isCreating}
                disabled={!name.trim() || !slug.trim()}
                className="flex-1"
                leftIcon={!isCreating ? <Building2 className="w-4 h-4" /> : undefined}
              >
                Create Workspace
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default CreateWorkspaceModal;
